import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Loader } from '@els/els-react--loader';
import ResultsList from '@Components/Results/ResultsList';
import { ResultsMap } from '@Containers/ResultsPreview/contexts';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import BlockLabel from '@Containers/ResultsPreview/Components/BlockLabel';
import BlockFeedbacks from '@Containers/ResultsPreview/Components/BlockFeedbacks';
import BlockActionButtons from '@Containers/ResultsPreview/Components/BlockActionButtons';
import { useHistory } from 'react-router-dom';
import { trackAIViewResults } from '@Containers/ResultsPreview/analytics';

function SubGroupMultipleDatabases({ previewItem, previewItemIndex, showMoreResults, redirectToResults, cancelQuery, isLast }) {
  const showProgressLoader = !get(previewItem, 'query.finished') && !get(previewItem, 'query.cancelled');
  const showQueryCancelled = get(previewItem, 'query.cancelled');
  const showViewResults = previewItem.resultsCount > 0;
  const showPredictionButton = get(previewItem, 'query.finished') && !get(previewItem, 'query.cancelled');
  const iconName = get(previewItem, 'title', '')?.toLowerCase().replace(' ', '-');

  const [expanded, setExpanded] = useState(false);
  const history = useHistory();
  const viewResultsButtonRef = useRef(null);
  const previewListRef = useRef(null);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (expanded) {
      showMoreResults(previewItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useDidUpdateEffect(() => {
    if (expanded) {
      previewListRef.current?.focus();
    } else {
      viewResultsButtonRef.current?.focus();
    }
  }, [expanded]);

  const resultsMap = useContext(ResultsMap);
  const results = resultsMap[previewItem.queryId];

  return (
    <li className="group-multiple-databases__sub-group e2e-quick-results-sub-item">
      <div className="results-preview__group">
        <BlockLabel previewItem={previewItem} showResultsCount showDatabaseLabel />
        <BlockFeedbacks previewItem={previewItem} />
        <BlockActionButtons
          previewItem={previewItem}
          showProgressLoader={showProgressLoader}
          onCancelClick={() => cancelQuery(previewItem.queryId)}
          showQueryCancelled={showQueryCancelled}
          showExpandButton={showViewResults}
          expandButtonConfig={{
            label: 'Preview Results',
            e2eName: 'preview-results',
          }}
          expandButtonAttrs={{
            'aria-label': `Preview results from ${previewItem.databaseLabel}`,
          }}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          showViewResultsButton={showViewResults}
          onViewResultsButtonClick={() => {
            trackAIViewResults(previewItem);
            redirectToResults(previewItem, history, previewItemIndex);
          }}
          viewResultsButtonRef={viewResultsButtonRef}
          showPredictionButton={showPredictionButton}
          showBioactivityVisualizationButton={false}
        />
      </div>

      {expanded && (
        <div
          id={previewItem.resultName}
          ref={previewListRef}
          tabIndex={-1}
          className={`quick-results-preview__item_preview u-els-margin-top quick-results-preview__item_preview-${iconName} group-multiple-databases__results`}>
          <h3 className="u-els-font-family-bold u-els-margin-bottom-1o2 u-els-font-size-h4">Top 3 results</h3>

          {results ? (
            <ResultsList metadata={previewItem} context={previewItem.context} results={results} size={results.length} hideCheckbox />
          ) : (
            <Loader isSmall isBlocking={false} className="results-preview__progress-loader" />
          )}
        </div>
      )}
      {!isLast && <hr className="c-els-divider group-multiple-databases__divider" />}
    </li>
  );
}

SubGroupMultipleDatabases.propTypes = {
  previewItem: PropTypes.object.isRequired,
  previewItemIndex: PropTypes.number,
  showMoreResults: PropTypes.func.isRequired,
  redirectToResults: PropTypes.func.isRequired,
  cancelQuery: PropTypes.func.isRequired,
  isLast: PropTypes.bool,
};

export default SubGroupMultipleDatabases;
